/**
 * The fan's GDPR withdrawal request at 3Halves: read it, raise it, confirm it, cancel it.
 *
 * Shaped like ./consent/provider.js and for the same reason -- everything it touches is injected,
 * so withdrawal.test.js drives every branch under `node --test` with no network and no clock.
 * `createBrowserWithdrawal()` at the foot of the file is the one place that reaches for the real
 * ones, and it exists so the two callers -- the leave funnel, and the join funnel's suppression check
 * and cancel -- cannot wire it differently.
 *
 * ---
 *
 * **EVERY ROUTE HERE ANSWERS INSIDE THE MIDDLEWARE'S `{success, data}` ENVELOPE**, and forgetting
 * that is what broke this module's first version. `confirm()` posted `{}` and was answered
 * `400 {"success":false,"error":"requestId is required"}`; the id it needed was sitting at
 * `data.requestId` on the raise it had just made, one level below where anything here was looking.
 * The same mistake cost ./leaveLoyalty.js's substituted-request warning, which compared
 * `body.eraseData` against a body that only ever had `success` and `data` on it.
 *
 * `@drewling/urc-loyalty-api`'s own `http.ts` knows this envelope. This client is a raw `fetch` and
 * never saw it. `unwrap()` below is where that is now paid for, once, so nothing downstream has to
 * know the envelope exists.
 *
 * ---
 *
 * **WHY THIS IS RAW `fetch` AND NOT A METHOD ON THE LOYALTY CLIENT.**
 *
 * `ILoyaltyAPI` has no leave, no withdraw and no consent record -- the library's own docblocks say
 * so, which is why its membership dialogs read no client at all. Adding these three calls to it
 * would also be self-defeating here: ./consentGate.js wraps every method on that interface, so the
 * instant the consent write lands, a withdrawal method would be refused by the decorator belonging
 * to the very consent it had just withdrawn. It would need a fourth CONSENT_EXEMPT entry and a
 * change to the test that asserts total coverage of `LoyaltyAPI.prototype`. Keeping the withdrawal
 * off the interface leaves both of those untouched.
 *
 * **THE TOKEN IS THE ACCESS TOKEN, NOT THE ID TOKEN.** These are 3Halves' own routes and their
 * audience is the loyalty API; ../loyalty/consent sends the ID token because that one is addressed
 * to this site. Sending either to the other is audience confusion, and it is the whole of D-068.
 *
 * ---
 *
 * **NOTHING HERE REJECTS, AND NOTHING HERE DELETES ANYTHING.**
 *
 * Every call resolves with an `OUTCOME`. That is not defensive coding: the caller's contract is that
 * a failed leg must never stop the fan leaving, so a rejection here must never be able to make
 * leaving harder than joining (D-097; this used to cite the preflight 500 every deployed origin but
 * dev got, which stopped by 2026-09-30).
 *
 * And what a successful call achieves is a row in the middleware's `withdrawalrequests` collection.
 * The executor that acts on it is admin-only and nothing schedules it, so nothing is ERASED at this
 * moment. One thing does move: with `eraseData`, the confirm releases the fan's login at once and
 * their next request is served from a new, empty account until the request is cancelled or run
 * (measured 2026-09-30). See docs/subsystems/loyalty-withdrawal.md before writing copy that says
 * otherwise.
 */

import { accessToken } from './urcAuth.js';

/** What a call can report. One vocabulary, so the funnel can publish whichever it got. */
export const OUTCOME = {
	/** The middleware accepted it. */
	OK: 'ok',
	/**
	 * 404. The fan has no record at the processor yet, so there is nothing to withdraw from --
	 * provisioning is asynchronous, and this is a legitimate window for a fan who signed up
	 * recently. A success from this flow's point of view, and deliberately not an error: it used to
	 * be a 500 carrying the caller's own address back, which was fixed on the processor's side.
	 */
	NOT_PROVISIONED: 'not-provisioned',
	/**
	 * No loyalty-audience bearer. `accessToken()` resolves '' rather than throwing whenever the SDK
	 * has nothing to give, so without this the request would go out as `Authorization: Bearer ` and
	 * come back 401 -- an error that reads as the processor refusing us when in fact we never had a
	 * credential to present.
	 */
	NO_CREDENTIAL: 'no-credential',
	/** The request did not land, whatever the reason. The caller carries on regardless. */
	INCOMPLETE: 'incomplete',
	/**
	 * 409 on the confirm. The request is not in a state that can be confirmed, which is worth its
	 * own value because it is the one failure with a defined repair: raise the request again to
	 * pick up whatever is current, then confirm that.
	 */
	CONFLICT: 'conflict',
};

/** Where the fan's own withdrawal lives on the middleware. */
const PATH = '/api/users/me/withdrawal';

/**
 * The code the middleware refused a SUPPRESSED fan with: one whose erasure request had completed.
 * Reported in 3Halves' run-6 test of 2026-09-23 as `422 {"success":false,"error":"We are unable to
 * create an account at this time.","code":"account_unavailable"}` on `GET /api/users/me`.
 *
 * **Every branch keyed on it is DEFENSIVE now.** 3Halves report the refusal removed on 2026-09-27
 * (their #660): an erased fan who comes back is answered `200` and given a new account. That is not
 * measured here -- it needs a fan whose erasure has completed, and only a 3Halves admin can complete
 * one -- and their own manual still describes suppression in other sections. So the branches stay:
 * each fires only on this explicit code, and costs nothing while no route sends it.
 */
const SUPPRESSED = 'account_unavailable';

/**
 * The id of a withdrawal request, read off an UNWRAPPED payload.
 *
 * Exported so that this module and ./leaveLoyalty.js name the wire field in exactly one place. It
 * is the field the confirm and the cancel are refused without, and the whole of this module's
 * history is somebody reading it off the wrong level of the response.
 *
 * @param {Object|null} body A withdrawal payload, as `unwrap()` leaves it.
 * @return {string} The id, or '' when there is none to be had.
 */
export function requestIdOf( body ) {
	if ( ! body || typeof body !== 'object' ) {
		return '';
	}

	return typeof body.requestId === 'string' ? body.requestId : '';
}

/**
 * Whether a result is the middleware refusing a fan it has SUPPRESSED -- a defensive check, see
 * SUPPRESSED above.
 *
 * As 3Halves described it until 2026-09-27, suppression followed a completed erasure and was
 * permanent. It did not wait for anybody to delete the fan's Auth0 account -- in the vendor's own
 * run that step was cleared with an evidence note and the account still signed in -- so a
 * suppressed fan could still reach this site with a perfectly valid token. That is the case
 * ./useJoinFunnel.js would have to catch before it writes anything, should the refusal come back.
 *
 * All three slots the middleware has put a code in are read, because this refusal is the one that
 * introduced the third: `error` carries prose here and the code sits in `code`.
 *
 * @param {Object|null} outcome A result from this module.
 * @return {boolean} True only for a refusal naming the suppressed state.
 */
export function isSuppressed( outcome ) {
	if ( ! outcome || outcome.outcome === OUTCOME.OK ) {
		return false;
	}

	const refusal = outcome.refusal;

	if ( ! refusal || typeof refusal !== 'object' ) {
		return false;
	}

	return [ refusal.reason, refusal.code, refusal.error ].includes( SUPPRESSED );
}

/**
 * What a rejoin needs to know from the processor BEFORE it writes the fan's consent back.
 *
 * One read of the fan's own withdrawal, answering two questions at once. Whether the fan is
 * suppressed, in which case the rejoin must write nothing at all: restoring `loyaltyoptin` for an
 * identity the processor refuses would re-open collection for an erased fan and leave their record
 * contradicting the processor's. That half is defensive -- see SUPPRESSED above. And the id of any request still open, which the
 * cancel needs and would otherwise spend its own lookup on -- so the join costs the same requests it
 * did before this existed.
 *
 * Never rejects, and fails OPEN: a read that did not land -- a refused preflight, no bearer, no
 * record -- reports "not suppressed, nothing to cancel", which is what the rejoin did before this
 * read existed.
 *
 * @param {{current: Function}} withdrawal A client from this module.
 * @return {Promise<{suppressed: boolean, requestId: string, refusal: Object|null}>} The verdict.
 */
export function checkBeforeRejoin( withdrawal ) {
	return Promise.resolve()
		.then( () => withdrawal.current() )
		.then(
			looked => ( {
				suppressed: isSuppressed( looked ),
				requestId:
					looked && looked.outcome === OUTCOME.OK ? requestIdOf( looked.body ) : '',
				refusal: looked ? looked.refusal : null,
			} ),
			() => ( { suppressed: false, requestId: '', refusal: null } )
		);
}

/**
 * Take the payload out of the middleware's `{success, data}` envelope.
 *
 * `data: null` is a real answer -- it is what `GET PATH` says for a fan with no open request -- so
 * the envelope check is on the SHAPE and not on the truthiness of what is inside it. A payload that
 * arrives unwrapped passes through untouched, which is what keeps this safe against a route that
 * answers bare.
 *
 * **Anything BESIDE `data` is dropped here, and one route puts something there.** The confirm
 * carries the fan's whole Art. 20 export as a top-level `dataExport` sibling of `data` (3Halves'
 * run-6 report, 2026-09-23), so it never reaches a caller. Nothing reads it today -- the export is
 * not offered on this host, see docs/subsystems/loyalty-withdrawal.md -- but whoever wires it must
 * read it off the parsed body before this runs, not off `result.body`.
 *
 * @param {*} parsed What `response.json()` gave.
 * @return {Object|null} The payload.
 */
function unwrap( parsed ) {
	if ( ! parsed || typeof parsed !== 'object' ) {
		return null;
	}

	if ( 'success' in parsed && 'data' in parsed ) {
		return parsed.data === undefined ? null : parsed.data;
	}

	return parsed;
}

/**
 * Build a withdrawal client over an injected transport.
 *
 * @param {Object}                 options               Wiring.
 * @param {string}                 options.baseUrl       The loyalty API origin, no trailing slash.
 * @param {() => Promise<string>}  options.getCredential The fan's ACCESS token, or ''.
 * @param {Function}               options.transport     A `fetch`-shaped function.
 * @param {number}                 [options.timeoutMs]   Per-call deadline.
 * @param {(ms: number) => Object} [options.abortAfter]  Signal source, for tests.
 * @return {{current: Function, raise: Function, confirm: Function, cancel: Function}} The client.
 */
export function createWithdrawal( {
	baseUrl,
	getCredential,
	transport,
	timeoutMs = 10000,
	abortAfter = defaultAbortAfter,
} ) {
	/**
	 * One call, and the only place a request is built.
	 *
	 * @param {string}      method HTTP method.
	 * @param {string}      path   Appended to baseUrl.
	 * @param {Object|null} body   JSON body, or null for none.
	 * @return {Promise<{outcome: string, status: number, body: Object|null, error: *, refusal: Object|null}>} Never rejects.
	 */
	function send( method, path, body ) {
		if ( ! baseUrl ) {
			// No gateway configured at all. The same state ./toasts/gate.js's gate 0 answers `off`
			// for, and a request against an empty base URL would be aimed at this site's own origin.
			return Promise.resolve( result( OUTCOME.INCOMPLETE, 0, null, null, null ) );
		}

		return Promise.resolve( typeof getCredential === 'function' ? getCredential() : '' ).then(
			credential => {
				if ( ! credential ) {
					return result( OUTCOME.NO_CREDENTIAL, 0, null, null, null );
				}

				return request( method, path, body, credential );
			}
		);
	}

	/**
	 * @param {string}      method     HTTP method.
	 * @param {string}      path       Appended to baseUrl.
	 * @param {Object|null} body       JSON body, or null.
	 * @param {string}      credential The fan's bearer.
	 * @return {Promise<Object>} A result. Never rejects.
	 */
	function request( method, path, body, credential ) {
		const options = {
			method,
			// The same posture as the consent provider: this is bearer-authenticated and reads no
			// cookie, and no cache layer may keep a per-fan answer to it.
			cache: 'no-store',
			credentials: 'omit',
			headers: {
				Authorization: `Bearer ${ credential }`,
				Accept: 'application/json',
			},
		};

		if ( body !== null ) {
			options.headers[ 'Content-Type' ] = 'application/json';
			options.body = JSON.stringify( body );
		}

		const signal = abortAfter( timeoutMs );

		if ( signal ) {
			options.signal = signal;
		}

		return transport( `${ baseUrl }${ path }`, options ).then( readResponse, error =>
			result( OUTCOME.INCOMPLETE, 0, null, error, null )
		);
	}

	/**
	 * @param {Object} response A fetch Response.
	 * @return {Promise<Object>} A result.
	 */
	function readResponse( response ) {
		const status = Number( response.status ) || 0;

		return readBody( response ).then( parsed => {
			if ( status >= 200 && status < 300 ) {
				// A body we cannot read is not a failed call: the request landed, which is the only
				// thing this flow needs from it. The body is a nicety -- it is what carries the id
				// the confirm is refused without, and what lets the caller notice that the
				// middleware handed back somebody else's existing request.
				return result( OUTCOME.OK, status, unwrap( parsed ), null, null );
			}

			// **The refusal envelope travels with the outcome, and that is the point of reading the
			// body on a non-2xx at all.** The first version discarded it, so a confirm refused with
			// `{"error":"requestId is required","code":"invalid_arguments"}` reached the console as
			// `{outcome:'incomplete', status:400, body:null}` -- which names nothing, and cost a
			// hand-driven reproduction to find. It is kept apart from `error` deliberately:
			// ../useLeaveLoyalty.js routes a truthy `error` through ./errorDetail.js, which reads an
			// Error and would reduce a plain refusal to `{kind:'Error', message:undefined}`.
			if ( status === 404 ) {
				return result( OUTCOME.NOT_PROVISIONED, status, null, null, parsed );
			}

			if ( status === 409 ) {
				return result( OUTCOME.CONFLICT, status, null, null, parsed );
			}

			return result( OUTCOME.INCOMPLETE, status, null, null, parsed );
		} );
	}

	/**
	 * The id to act on: the one we were handed, or whatever the middleware currently holds.
	 *
	 * The lookup is the fallback and not the rule. `./leaveLoyalty.js` has just raised the request
	 * and knows the id, so the leave path still costs exactly two calls; the join path's cancel has
	 * no raise to read one off and always pays for this one.
	 *
	 * @param {string} [requestId] An id already in hand.
	 * @return {Promise<{id: string, looked: Object|null}>} The id, and the lookup's own result where
	 *                                                     one was made -- so a caller can report
	 *                                                     WHY there was no id rather than guessing.
	 */
	function resolveRequestId( requestId ) {
		if ( typeof requestId === 'string' && requestId !== '' ) {
			return Promise.resolve( { id: requestId, looked: null } );
		}

		return send( 'GET', PATH, null ).then( looked => ( {
			id: requestIdOf( looked.body ),
			looked,
		} ) );
	}

	return {
		/**
		 * The fan's open request, or nothing.
		 *
		 * `body` is the request where there is one and `null` where there is not -- the middleware
		 * answers `{"success": true, "data": null}` for a fan with nothing open, which `unwrap()`
		 * flattens to the same `null` an unreadable body gives.
		 *
		 * @return {Promise<Object>} A result.
		 */
		current() {
			return send( 'GET', PATH, null );
		},

		/**
		 * Step one: record the request and take back the impact summary. Destroys nothing.
		 *
		 * Asking twice returns the EXISTING request rather than creating a second, which is what
		 * makes a double-click and a retry both free -- and is also why the caller compares what
		 * came back against what it asked for.
		 *
		 * @param {Object} input `{withdrawProgramme, eraseData}`.
		 * @return {Promise<Object>} A result. Its `body` carries the `requestId` step two needs.
		 */
		raise( input ) {
			return send( 'POST', PATH, {
				withdrawProgramme: input.withdrawProgramme !== false,
				eraseData: input.eraseData === true,
			} );
		},

		/**
		 * Step two. Idempotent, and it still moves nothing: it records the fan's karma and tier
		 * onto the request as the figures they were shown when they said yes.
		 *
		 * **The id is required and the route says so.** Called without one it looks the open request
		 * up rather than posting a body that is certain to be refused, and where there is nothing
		 * open it reports that without spending a call at all.
		 *
		 * @param {string} [requestId] The id from the raise. Looked up when absent.
		 * @return {Promise<Object>} A result.
		 */
		confirm( requestId ) {
			return resolveRequestId( requestId ).then( ( { id, looked } ) => {
				if ( id ) {
					return send( 'POST', `${ PATH }/confirm`, { requestId: id } );
				}

				// The lookup failed, so it already says why -- no bearer, no record at the
				// processor, no gateway. Better than overwriting all three with `incomplete`.
				if ( looked && looked.outcome !== OUTCOME.OK ) {
					return looked;
				}

				// The lookup worked and there is nothing open. We raised one a moment ago, so this
				// is a genuine shortfall rather than a quiet success.
				return result(
					OUTCOME.INCOMPLETE,
					looked ? looked.status : 0,
					null,
					null,
					looked ? looked.refusal : null
				);
			} );
		},

		/**
		 * Cancel an open request.
		 *
		 * Called from the JOIN path rather than from here, and it is not housekeeping: a fan who
		 * leaves and then rejoins would otherwise still have a request standing that somebody could
		 * execute weeks later, against an account that is back in the programme.
		 *
		 * The DELETE is under the same `requestId` contract the confirm turned out to be. The join
		 * path hands the id over from `checkBeforeRejoin()`'s read; called without one, this looks
		 * it up itself. A fan with no open request is not a failure -- that result is `ok` with
		 * nothing sent, which is exactly what the caller wants to hear.
		 *
		 * @param {string} [requestId] The id of the open request. Looked up when absent.
		 * @return {Promise<Object>} A result. Never rejects.
		 */
		cancel( requestId ) {
			return resolveRequestId( requestId ).then( ( { id, looked } ) => {
				if ( ! id ) {
					return looked;
				}

				return send( 'DELETE', PATH, { requestId: id } );
			} );
		},
	};
}

/**
 * Read a response body without letting an unreadable one become a failure.
 *
 * Read on EVERY status, not just a 2xx: a refusal's envelope is the only thing that names what the
 * middleware objected to.
 *
 * @param {Object} response A fetch Response.
 * @return {Promise<*>} The parsed body, or null.
 */
function readBody( response ) {
	if ( typeof response.json !== 'function' ) {
		return Promise.resolve( null );
	}

	return Promise.resolve()
		.then( () => response.json() )
		.then(
			parsed => parsed,
			() => null
		);
}

/**
 * @param {string}      outcome An OUTCOME.
 * @param {number}      status  HTTP status, or 0 when there was none.
 * @param {Object|null} body    The parsed payload, unwrapped, where the call succeeded.
 * @param {*}           error   What the transport threw, where it threw.
 * @param {Object|null} refusal The server's own envelope on a non-2xx, for the log.
 * @return {Object} The result.
 */
function result( outcome, status, body, error, refusal ) {
	return { outcome, status, body, error, refusal };
}

/**
 * The default deadline, which is `AbortSignal.timeout` where the engine has it.
 *
 * Guarded rather than assumed: this module is imported by a `node --test` suite, and a browser
 * without it should lose the deadline rather than the request.
 *
 * @param {number} ms How long to allow.
 * @return {Object|null} A signal, or null.
 */
function defaultAbortAfter( ms ) {
	if ( typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function' ) {
		return null;
	}

	return AbortSignal.timeout( ms );
}

/**
 * The client this site actually uses.
 *
 * One factory rather than two call sites assembling the same thing, because the two are in opposite
 * flows and would drift: ./useLeaveLoyalty.js raises and confirms, and ./useJoinFunnel.js reads and
 * cancels.
 * A cancel that reached a different origin, or went out with the ID token, would fail quietly and
 * leave a request standing against a fan who has just rejoined.
 *
 * @return {{current: Function, raise: Function, confirm: Function, cancel: Function}} The client.
 */
export function createBrowserWithdrawal() {
	const config = ( typeof window !== 'undefined' && window.urcLoyalty ) || {};

	return createWithdrawal( {
		baseUrl: config.baseUrl || '',
		// The ACCESS token. These are 3Halves' routes and their audience is the loyalty API; the ID
		// token is addressed to this site and belongs on this site's own endpoints. D-068.
		getCredential: accessToken,
		transport: ( url, options ) => globalThis.fetch( url, options ),
	} );
}
